import React, { useEffect, useState } from 'react'
import { Button, Card, Checkbox, Flex, Space, Tag, Typography } from 'antd'
import { ArrowLeftOutlined, ArrowRightOutlined, CheckCircleFilled } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { ArrowRight } from 'lucide-react'
import { Company } from '../../../../types/company/Company'
import { RankedCandidate, SiblingCandidates } from '../../utils/groupSuggestionsBySibling'
import { CONFIDENT_THRESHOLD, RowResolution } from '../../utils/buildSiblingDecision'
import SourceRecordName from '../SourceRecordName'
import StepHeading from './StepHeading'

interface WizardStepReviewProps {
  target: Company
  reviewSiblings: Company[]
  index: number
  candidatesBySibling: Map<string, SiblingCandidates>
  resolutions: Record<string, RowResolution>
  onResolve: (siblingId: string, resolution: RowResolution) => void
  onSkipBoth: (siblingId: string) => void
  onPrev: () => void
  onNext: () => void
}

interface CandidateSectionProps {
  step: number
  siblingName: string
  role: string
  companyId: string
  sourceEntity: 'Customer' | 'Supplier'
  candidates: RankedCandidate[]
  value: string[]
  onChange: (value: string[]) => void
}

const CandidateSection: React.FC<CandidateSectionProps> = ({
  step,
  siblingName,
  role,
  companyId,
  sourceEntity,
  candidates,
  value,
  onChange
}) => {
  const toggle = (sourceId: string) =>
    onChange(value.includes(sourceId) ? value.filter(id => id !== sourceId) : [...value, sourceId])

  return (
    <>
      <Flex align="baseline" gap={6} style={{ marginBottom: 7 }}>
        <Typography.Text type="secondary" style={{ fontSize: 10 }}>
          {step} ·
        </Typography.Text>
        <Typography.Text strong style={{ fontSize: 15 }}>
          {siblingName}
        </Typography.Text>
        <Typography.Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase' }}>
          {role}
        </Typography.Text>
      </Flex>
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        {candidates.map(candidate => {
          const selected = value.includes(candidate.sourceId)
          const confidentMatch = candidate.confidence >= CONFIDENT_THRESHOLD
          return (
            <Card
              key={candidate.sourceId}
              size="small"
              hoverable
              onClick={() => toggle(candidate.sourceId)}
              style={{
                cursor: 'pointer',
                borderColor: selected ? '#2a78d6' : undefined,
                borderWidth: selected ? 2 : 1,
                background: selected ? '#eef4fc' : undefined
              }}
            >
              <Flex justify="space-between" align="center" gap={12}>
                <Flex align="center" gap={10}>
                  <Checkbox checked={selected} onClick={() => toggle(candidate.sourceId)} />
                  <Typography.Text strong={selected}>
                    <SourceRecordName companyId={companyId} sourceEntity={sourceEntity} sourceId={candidate.sourceId} />
                  </Typography.Text>
                  {selected && <CheckCircleFilled style={{ color: '#2a78d6' }} />}
                </Flex>
                <Tag color={confidentMatch ? 'success' : 'gold'} style={{ marginInlineEnd: 0 }}>
                  {Math.round(candidate.confidence * 100)} %
                </Tag>
              </Flex>
            </Card>
          )
        })}
      </Space>
    </>
  )
}

const WizardStepReview: React.FC<WizardStepReviewProps> = ({
  target,
  reviewSiblings,
  index,
  candidatesBySibling,
  resolutions,
  onResolve,
  onSkipBoth,
  onPrev,
  onNext
}) => {
  const { t } = useTranslation()
  const sibling = reviewSiblings[index]
  const candidates = candidatesBySibling.get(sibling.id) ?? { customer: [], supplier: [] }

  const [customerChoice, setCustomerChoice] = useState<string[]>([])
  const [supplierChoice, setSupplierChoice] = useState<string[]>([])

  useEffect(() => {
    const existing = resolutions[sibling.id]
    setCustomerChoice(existing?.customer ?? (candidates.customer[0] ? [candidates.customer[0].sourceId] : []))
    setSupplierChoice(existing?.supplier ?? (candidates.supplier[0] ? [candidates.supplier[0].sourceId] : []))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sibling.id])

  const confirmBoth = () => {
    onResolve(sibling.id, { customer: customerChoice, supplier: supplierChoice })
    onNext()
  }

  const skipBoth = () => {
    onSkipBoth(sibling.id)
    onNext()
  }

  const goPrev = () => {
    onResolve(sibling.id, { customer: customerChoice, supplier: supplierChoice })
    onPrev()
  }

  return (
    <>
      <StepHeading
        title={t('groupCustomerMapping:wizardReviewTitle', 'Epävarma {{index}} / {{count}}', {
          index: index + 1,
          count: reviewSiblings.length
        })}
        extra={
          <Typography.Text>
            {target.name} <ArrowRight size={14} style={{ verticalAlign: 'middle' }} /> {sibling.name}
          </Typography.Text>
        }
        description={t(
          'groupCustomerMapping:wizardReviewDescription',
          'Asiakas ja toimittaja valitaan erikseen — ne ovat lähdejärjestelmässä eri tietueita. Uusia ei luoda; ohita jos vastinetta ei ole.'
        )}
      />

      <div style={{ marginBottom: 16 }}>
        {candidates.customer.length > 0 ? (
          <CandidateSection
            step={1}
            siblingName={sibling.name}
            role={t('groupCustomerMapping:wizardReviewCustomerSection', 'Asiakkaana')}
            companyId={target.id}
            sourceEntity="Customer"
            candidates={candidates.customer}
            value={customerChoice}
            onChange={setCustomerChoice}
          />
        ) : (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('groupCustomerMapping:wizardReviewSkipCustomer', 'Ei vastinetta — ohita asiakas')}
          </Typography.Text>
        )}
      </div>

      <div style={{ marginBottom: 16 }}>
        {candidates.supplier.length > 0 ? (
          <CandidateSection
            step={2}
            siblingName={sibling.name}
            role={t('groupCustomerMapping:wizardReviewSupplierSection', 'Toimittajana')}
            companyId={target.id}
            sourceEntity="Supplier"
            candidates={candidates.supplier}
            value={supplierChoice}
            onChange={setSupplierChoice}
          />
        ) : (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('groupCustomerMapping:wizardReviewSkipSupplier', 'Ei vastinetta — ohita toimittaja')}
          </Typography.Text>
        )}
      </div>

      <Flex justify="space-between" align="center">
        <Button onClick={skipBoth}>{t('groupCustomerMapping:wizardReviewSkipBoth', 'Ohita molemmat')}</Button>
        <Flex gap={8}>
          <Button icon={<ArrowLeftOutlined />} onClick={goPrev}>
            {t('groupCustomerMapping:wizardReviewPrev', 'Edellinen')}
          </Button>
          <Button type="primary" icon={<ArrowRightOutlined />} iconPosition="end" onClick={confirmBoth}>
            {t('groupCustomerMapping:wizardReviewConfirm', 'Jatka')}
          </Button>
        </Flex>
      </Flex>
    </>
  )
}

export default WizardStepReview
